import Competence from "constants/Competence";
import type { TransfertDto } from "models";
import type { CodeMetier, MetierWithConditions } from "models/ContactsMetiers";

function isSdeaCompetentOrFalse(
  conditions: Array<string> | Competence,
  transferts?: Array<TransfertDto>
): boolean {
  if (transferts == null || transferts.length === 0) {
    return false;
  } else if (Array.isArray(conditions)) {
    return transferts.some((t) => conditions.includes(t.code));
  } else {
    return transferts.some((t) => t.competence === conditions);
  }
}

export const metiersConditions: Record<
  CodeMetier,
  Pick<MetierWithConditions, "ordreAffichage" | "isSdeaCompetentCondition">
> = {
  "COM-ASS-CSE": {
    ordreAffichage: 100,
    isSdeaCompetentCondition: (transferts) =>
      isSdeaCompetentOrFalse(["ASS-CE-2", "ASS-CE-6"], transferts),
  },
  "COM-ASS-BRA": {
    ordreAffichage: 200,
    isSdeaCompetentCondition: (transferts) =>
      isSdeaCompetentOrFalse(["ASS-EX-2", "ASS-EX-10", "ASS-EX-14"], transferts),
  },
  "COM-ASS-ETU": {
    ordreAffichage: 300,
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.ASS, transferts),
  },
  "COM-ASS-VERI": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(["ASS-CE-2"], transferts),
  },
  "COM-ASS-ANC": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(["ASS-AC-1"], transferts),
  },
  "COM-ASS-TES": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(["ASS-CE-5"], transferts),
  },
  "COM-ASS-TOP": {
    isSdeaCompetentCondition: (transferts) =>
      isSdeaCompetentOrFalse(["ASS-CE-2", "ASS-CE-6"], transferts),
  },
  "COM-ASS-EMP": {
    isSdeaCompetentCondition: (transferts) =>
      isSdeaCompetentOrFalse(["ASS-CE-2", "ASS-CE-5", "ASS-CE-6"], transferts),
  },
  "COM-EAU-CST": {
    ordreAffichage: 100,
    isSdeaCompetentCondition: (transferts) =>
      isSdeaCompetentOrFalse(["EAU-CE-6", "EAU-CE-3"], transferts),
  },
  "COM-EAU-BRA": {
    ordreAffichage: 200,
    isSdeaCompetentCondition: (transferts) =>
      isSdeaCompetentOrFalse(["EAU-EX-3", "EAU-EX-10"], transferts),
  },
  "COM-EAU-ETU": {
    ordreAffichage: 300,
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.EAU, transferts),
  },
  "COM-EAU-CQE": {
    ordreAffichage: 400,
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.EAU, transferts),
  },
  "COM-EAU-TSIG": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.EAU, transferts),
  },
  "COM-EAU-TOU": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.EAU, transferts),
  },
  "COM-EAU-CPME": {
    isSdeaCompetentCondition: (transferts) =>
      isSdeaCompetentOrFalse(["EAU-ET-3", "EAU-ET-4", "EAU-ET-6", "EAU-ET-8"], transferts),
  },
  "COM-GCE-TRI": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.GCE, transferts),
  },
  "COM-GCE-GDI": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.GCE, transferts),
  },
  "COM-GCE-CP": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.GCE, transferts),
  },
  "COM-GCE-PAPI": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.GCE, transferts),
  },
  "COM-GCE-PSEC": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.GCE, transferts),
  },
  "COM-GCE-SAGE": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.GCE, transferts),
  },
  "COM-GCE-AME": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.GCE, transferts),
  },
  "COM-GCE-THD": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.GCE, transferts),
  },
  "COM-ALL-AFA": {
    ordreAffichage: 100,
    isSdeaCompetentCondition: (transferts) =>
      isSdeaCompetentOrFalse(
        [
          "EAU-AB-0",
          "EAU-AB-3",
          "EAU-AB-4",
          "EAU-AB-6",
          "ASS-AB-0",
          "ASS-AB-2",
          "ASS-AB-5",
          "ASS-AB-6",
        ],
        transferts
      ),
  },
  "PER-ALL-RAFT": { isSdeaCompetentCondition: (_) => true },
  "PER-ALL-DIRP": { isSdeaCompetentCondition: (_) => true },
  "PER-EAU-CPMA": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.EAU, transferts),
  },
  "TER-ALL-DIRT": { isSdeaCompetentCondition: (_) => true },
  "TER-GEPU-REF": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.GEPU, transferts),
  },
  "TER-GEPU-TECH": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.GEPU, transferts),
  },
  "TER-ASS-CDP": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.ASS, transferts),
  },
  "TER-ASS-ASDO": {
    isSdeaCompetentCondition: (transferts) => isSdeaCompetentOrFalse(Competence.ASS, transferts),
  },
};
